import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import ButtonComponent from '../../components/button/ButtonComponent';
import { Loader } from '../../components/loader';
import { AddBankAccountDrawer } from '../../components/make-payment/AddBankAccountDrawer';
import { ApplyToAllModal } from '../../components/make-payment/ApplyToAllModal';
import { CardIneligibleContractsPanel } from '../../components/make-payment/CardIneligibleContractsPanel';
import { ContractSidebar } from '../../components/make-payment/ContractSidebar';
import { FullyCoveredContractsPanel } from '../../components/make-payment/FullyCoveredContractsPanel';
import { FullyPaidContractsPanel } from '../../components/make-payment/FullyPaidContractsPanel';
import { PaymentConfirmationModal } from '../../components/make-payment/PaymentConfirmationModal';
import { PaymentMethodSelector } from '../../components/make-payment/PaymentMethodSelector';
import { PaymentRowsTable } from '../../components/make-payment/PaymentRowsTable';
import AddPaymentMethodModal from '../../components/modals/AddPaymentMethodModal';
import { colors } from '../../components/theme/theme';
import CommonHeader from '../../components/ui-components/CommonHeader';
import { useAddBankAccountForm } from '../../hooks/make-payment/useAddBankAccountForm';
import { useApplyToAllModals } from '../../hooks/make-payment/useApplyToAllModals';
import { useCardFees } from '../../hooks/make-payment/useCardFees';
import { useContractPaymentData } from '../../hooks/make-payment/useContractPaymentData';
import { useCountryMismatchCheck } from '../../hooks/make-payment/useCountryMismatchCheck';
import { useDuplicatePaymentCheck } from '../../hooks/make-payment/useDuplicatePaymentCheck';
import { usePaymentRows } from '../../hooks/make-payment/usePaymentRows';
import { usePaymentSubmission } from '../../hooks/make-payment/usePaymentSubmission';
import { useSavedPaymentMethods } from '../../hooks/make-payment/useSavedPaymentMethods';
import { useStripeSurchargeCalculation } from '../../hooks/make-payment/useStripeSurchargeCalculation';
import { createStripeSetupIntent } from '../../services/payment-services';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { formatCurrency } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import {
    addAccountButtonSx,
    cancelButtonSx,
    cardFeesErrorBoxSx,
    cardMinimumAmountInfoBoxSx,
    emptyStateBoxSx,
    payButtonSx,
    paymentContentBoxSx,
    paymentFooterLeftSx,
} from './DashboardStyles';

import type { AmountChoice, PaymentMethodType, PaymentRow } from '../../hooks/make-payment/usePaymentRows';
import type { OpenInvoiceApiResponse } from '../../services/payment-services';

const DashboardMakePayment: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const userDetails = useSelector((state: RootState) => state.user.userDetails);
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const isStripeEnabled = true; // Temporary: force enabled for localhost testing

    // Get selected contract IDs from navigation state
    const selectedContractIds = useMemo<string[]>(
        () => location.state?.selectedContractIds ?? [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.state],
    );
    const isPayAll: boolean = location.state?.payAll || false;
    const fromDashboard: boolean = location.state?.fromDashboard || false;
    const fromPayments: boolean = location.state?.fromPayments || false;
    const fromContracts: boolean = location.state?.fromContracts || false;
    const fromDocuments: boolean = location.state?.fromDocuments || false;
    const fromBankAccounts: boolean = location.state?.fromBankAccounts || false;
    const prefillAmounts = useMemo<Record<string, string>>(
        () => location.state?.prefillAmounts ?? {},
        // location.state is set once at navigation time and never mutates
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.state],
    );
    // Set when Pay Now was clicked on a single invoice row, so that invoice's own due/scheduled
    // amounts are used instead of summing every open invoice/scheduled payment for the contract.
    const singleInvoiceOverrides = useMemo<Record<string, { dueAmount: string; scheduledAmount: string }>>(
        () => location.state?.singleInvoiceOverrides ?? {},
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.state],
    );

    const {
        activeContracts,
        bankAccountOptions,
        openInvoices,
        pendingScheduleAmount,
        isLoadingData,
        userContractsCountryCombinations,
        getBankAccounts,
        getTotalAmountForContract,
        getScheduledAmountForContract,
        canSelectContract,
        getContractById,
        resolveContractForPaymentRow,
        isConsumerLoanContract,
        getContractTaxAddress,
        isContractFullyScheduled,
        hasActiveScheduledPayment,
        getOpenInvoicesData,
        getScheduledPaymentsData,
        getUserContractsData,
    } = useContractPaymentData();

    // Re-fetches contracts/open-invoices/scheduled-payments so due amounts and "already scheduled"
    // banners reflect reality after a bulk card payment partially succeeds and the user stays on
    // this page (a full success instead navigates away to a page that fetches fresh on its own).
    const refreshPaymentData = useCallback(
        () => Promise.all([getUserContractsData(), getOpenInvoicesData(), getScheduledPaymentsData()]).then(() => {}),
        [getUserContractsData, getOpenInvoicesData, getScheduledPaymentsData],
    );

    // Identify contracts with fully covered balance: scheduled invoices + pending amount >= outstanding
    const fullyCoveredContracts = useMemo(() => {
        const covered = activeContracts.filter(contract => {
            const dbContractId = contract.Id?.toString() || '';
            const aspireId = contract.AspireId || `Contract ${contract.Id}`;
            const outstandingBalance = parseFloat(String(contract.OutstandingBalance || '0'));

            // Sum scheduled invoices (is_scheduled = true)
            const scheduledInvoices = openInvoices.reduce((sum: number, inv: OpenInvoiceApiResponse) => {
                const invDbId = String(inv?.contractId || inv?.ContractId || '')
                    .trim()
                    .toUpperCase();
                const checkDbId = dbContractId.trim().toUpperCase();
                const isScheduled = inv?.is_scheduled === true || inv?.IsScheduled === true;

                if (invDbId === checkDbId && isScheduled) {
                    return sum + parseFloat(String(inv.amount || inv.Amount || '0'));
                }
                return sum;
            }, 0);

            // Get pending schedule amount for this contract
            const pendingAmount = pendingScheduleAmount.reduce((sum: number, pending) => {
                if (String(pending.contractId).trim() === dbContractId.trim()) {
                    return sum + parseFloat(String(pending.totalPendingScheduleAmount || '0'));
                }
                return sum;
            }, 0);

            const totalScheduledAmount = scheduledInvoices + pendingAmount;
            const isCovered = totalScheduledAmount >= outstandingBalance && totalScheduledAmount > 0;

            if (isCovered) {
                console.log(
                    `[FullyCovered] ${aspireId}: scheduled=${scheduledInvoices}, pending=${pendingAmount}, total=${totalScheduledAmount}, outstanding=${outstandingBalance}`,
                );
            }
            return isCovered;
        });
        console.log('[FullyCovered] Total covered contracts:', covered.length);
        return covered;
    }, [activeContracts, openInvoices, pendingScheduleAmount]);

    // Identify fully paid contracts: RemainingPrincipal <= 0 OR RemainingPayments <= 0
    const fullyPaidContracts = useMemo(() => {
        return activeContracts.filter(contract => {
            // Check if all principal has been paid
            const remainingPrincipal =
                contract.RemainingPrincipal != null ? parseFloat(String(contract.RemainingPrincipal)) : null;
            if (remainingPrincipal !== null && remainingPrincipal <= 0) {
                return true;
            }

            // Check if all payments have been made
            const remainingPayments =
                contract.RemainingPayments != null ? parseFloat(String(contract.RemainingPayments)) : null;
            if (remainingPayments !== null && remainingPayments <= 0) {
                return true;
            }

            return false;
        });
    }, [activeContracts]);

    // Wrapper around canSelectContract to exclude fully covered and fully paid contracts
    const canSelectContractFiltered = useCallback(
        (contractId: string): boolean => {
            if (!canSelectContract(contractId)) return false;
            // Exclude fully covered contracts
            const isFullyCovered = fullyCoveredContracts.some(c => {
                const cId = c.AspireId || `Contract ${c.Id}`;
                return cId === contractId || c.Id?.toString() === contractId;
            });
            if (isFullyCovered) return false;
            // Exclude fully paid contracts
            const isFullyPaid = fullyPaidContracts.some(c => {
                const cId = c.AspireId || `Contract ${c.Id}`;
                return cId === contractId || c.Id?.toString() === contractId;
            });
            return !isFullyPaid;
        },
        [canSelectContract, fullyCoveredContracts, fullyPaidContracts],
    );

    // Combine hidden contract IDs for the sidebar
    const hiddenContractIds = useMemo(() => {
        const hidden = new Set<string>();
        fullyCoveredContracts.forEach(c => {
            const cId = c.AspireId || `Contract ${c.Id}`;
            hidden.add(cId);
        });
        fullyPaidContracts.forEach(c => {
            const cId = c.AspireId || `Contract ${c.Id}`;
            hidden.add(cId);
        });
        return Array.from(hidden);
    }, [fullyCoveredContracts, fullyPaidContracts]);

    const {
        paymentRows,
        setPaymentRows,
        userSelectedContractIdsRef,
        paymentRowErrors,
        setPaymentRowErrors,
        paymentSubmitErrors,
        setPaymentSubmitErrors,
        paymentDatePickerErrors,
        setPaymentDatePickerErrors,
        setOtherAmountInputRef,
        buildPaymentRow,
        buildContractWidePaymentRow,
        handleDateChange,
        handleDatePickerError,
        handleDeleteRow,
        handleAmountChoiceChange,
        getEffectivePaymentAmount,
        getDbContractIdForPayload,
    } = usePaymentRows({
        activeContracts,
        getContractById,
        getTotalAmountForContract,
        getScheduledAmountForContract,
        prefillAmounts,
        singleInvoiceOverrides,
        isConsumerLoanContract,
        isLoadingData,
        openInvoices,
        isPayAll,
        selectedContractIds,
        canSelectContract: canSelectContractFiltered,
        t,
    });

    // Filter out fully covered and fully paid contracts from payment rows display
    const filteredPaymentRows = useMemo(() => {
        const fullyCoveredIds = new Set(
            fullyCoveredContracts.map(c => {
                const cId = c.AspireId || `Contract ${c.Id}`;
                return cId.toUpperCase();
            }),
        );
        const fullyPaidIds = new Set(
            fullyPaidContracts.map(c => {
                const cId = c.AspireId || `Contract ${c.Id}`;
                return cId.toUpperCase();
            }),
        );

        console.log('[FilteredRows] Fully covered IDs:', Array.from(fullyCoveredIds));
        console.log('[FilteredRows] Fully paid IDs:', Array.from(fullyPaidIds));
        console.log('[FilteredRows] Original payment rows:', paymentRows.length);

        const filtered = paymentRows.filter(row => {
            const rowId = String(row.contractId).toUpperCase();
            const isCovered = fullyCoveredIds.has(rowId);
            const isPaid = fullyPaidIds.has(rowId);
            if (isCovered || isPaid) {
                console.log(`[FilteredRows] Filtering out ${rowId} (covered=${isCovered}, paid=${isPaid})`);
            }
            return !isCovered && !isPaid;
        });

        console.log('[FilteredRows] Filtered payment rows:', filtered.length);
        return filtered;
    }, [paymentRows, fullyCoveredContracts, fullyPaidContracts]);

    // Clears stale error banners from a previous failed payment attempt whenever the user makes a
    // fresh navigation to this page (including browser back/forward) — location.key changes on
    // every real navigation entry but NOT on an in-place data refresh (e.g. refreshPaymentData
    // after a partial payment success), so this won't wipe out errors we just set for that case.
    useEffect(() => {
        setPaymentRowErrors([]);
        setPaymentSubmitErrors([]);
        setPaymentDatePickerErrors([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    // Contracts are already fetched on mount by useContractPaymentData's getUserContractsData
    // (as part of its Promise.all below) — no separate fetch needed here.

    const [, setSelectedPaymentContracts] = useState<string[]>([]);

    const [addPaymentMethodModalOpen, setAddPaymentMethodModalOpen] = useState(false);
    const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodType>('bank_account');

    const { savedPaymentMethods, fetchStripePaymentMethods } = useSavedPaymentMethods(isLoadingData);

    const getCardCountryByPaymentMethodId = (paymentMethodId: string): string | undefined => {
        const card = savedPaymentMethods.find(m => m.id === paymentMethodId);
        return card?.country;
    };

    const {
        cardAdditionalFees,
        cardFeesLoading,
        setHasFetchedCardFees,
        cardFeesError,
        fetchCardFees,
        isCardUnavailableForContract,
        cardIneligibleContracts,
        cardFeeByRow,
        getRowCardFeeAmount,
        totalCardFee,
    } = useCardFees({
        userContractsCountryCombinations,
        getContractById,
        activeContracts,
        canSelectContract,
        paymentMethodType,
        paymentRows,
        setPaymentRows,
        setSelectedPaymentContracts,
        setPaymentRowErrors,
        setPaymentDatePickerErrors,
        getEffectivePaymentAmount,
        getCardCountryByPaymentMethodId,
        getTotalAmountForContract,
        getScheduledAmountForContract,
        t,
    });

    const { duplicatePaymentCheckByRow } = useDuplicatePaymentCheck({
        paymentMethodType,
        paymentRows,
        isContractFullyScheduled,
        hasActiveScheduledPayment,
        t,
    });

    useStripeSurchargeCalculation({
        isLoadingData,
        activeContracts,
        openInvoices,
        isPayAll,
        selectedContractIds,
        getTotalAmountForContract,
        canSelectContract,
        getContractTaxAddress,
    });

    const {
        drawerOpen,
        setDrawerOpen,
        formData,
        formErrors,
        setFormErrors,
        country,
        setCountry,
        showAccountNumber,
        setShowAccountNumber,
        showReEnterAccountNumber,
        setShowReEnterAccountNumber,
        showRoutingNumber,
        setShowRoutingNumber,
        showReEnterRoutingNumber,
        setShowReEnterRoutingNumber,
        accountTypeOptions,
        handleInputChange,
        handleAddAccount,
        handleCloseDrawer,
    } = useAddBankAccountForm({ getBankAccounts });

    const {
        showBankAccountModal,
        handleBankAccountChange,
        handleCloseBankAccountModal,
        handleApplyBankAccountToAll,
        handleApplyBankAccountToCurrentOnly,
        showApplyCardModal,
        handlePaymentCardChange,
        handleCloseApplyCardModal,
        handleApplyCardToAll,
        handleApplyCardToCurrentOnly,
    } = useApplyToAllModals({ paymentRows, setPaymentRows, setPaymentRowErrors, setPaymentSubmitErrors, cardFeeByRow });

    const hasCountryMismatch = useCountryMismatchCheck(paymentRows, bankAccountOptions, getContractById);

    const {
        showConfirmationModal,
        handleMakePayment,
        handlePayWithCard,
        handleConfirmPayment,
        handleCloseConfirmationModal,
    } = usePaymentSubmission({
        paymentRows,
        activeContracts,
        getContractById,
        getEffectivePaymentAmount,
        getDbContractIdForPayload,
        paymentDatePickerErrors,
        setPaymentRowErrors,
        setPaymentSubmitErrors,
        refreshPaymentData,
        userDetails,
        savedPaymentMethods,
        cardAdditionalFees,
        t,
        impersonation,
    });

    const handleBack = () => {
        if (fromDashboard) {
            navigate(NavigationConfig.Home);
        } else if (fromPayments) {
            navigate(NavigationConfig.Payments);
        } else if (fromDocuments) {
            navigate(NavigationConfig.DocumentOverview);
        } else if (fromBankAccounts) {
            navigate(NavigationConfig.BankAccounts);
        } else if (fromContracts) {
            navigate(-1);
        } else {
            navigate(NavigationConfig.Home);
        }
    };

    const handleCancel = () => {
        if (fromDashboard) {
            navigate(NavigationConfig.Home);
        } else if (fromPayments) {
            navigate(NavigationConfig.Payments);
        } else if (fromContracts) {
            navigate(-1);
        } else {
            navigate(NavigationConfig.Home);
        }
    };

    const handlePaymentMethodChange = async (method: PaymentMethodType) => {
        // Prevent switching to card if Stripe is disabled
        if (method === 'card' && !isStripeEnabled) {
            return;
        }

        // Card payments are contract-wide (applied to the oldest open invoices), so a row opened via a
        // single invoice's "Pay Now" is re-based to the whole contract's due/scheduled amounts.
        const cardRows: PaymentRow[] = paymentRows.map(row => {
            const contractWide = buildContractWidePaymentRow(row.contractId);
            return {
                ...row,
                amountChoice: 'invoice' as AmountChoice,
                dueAmount: contractWide.dueAmount,
                scheduledAmount: contractWide.scheduledAmount,
                paymentAmount: contractWide.paymentAmount,
            };
        });

        // Validate card payment minimum amount: check subtotal of all payment rows
        if (method === 'card') {
            const MINIMUM_CARD_PAYMENT = 300;
            const subtotal = cardRows.reduce((sum, row) => {
                const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
                const choice = row.amountChoice ?? 'invoice';
                const hasInvoices = hasContractInvoices(row.contractId);

                if (choice === 'invoice' && !hasInvoices) return sum;

                return sum + getEffectivePaymentAmount(row, contract ?? undefined);
            }, 0);

            if (subtotal < MINIMUM_CARD_PAYMENT) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: t('makePayment.cardMinimumPaymentRequired'),
                        severity: 'error',
                    }),
                );
                return;
            }
        }

        dispatch(showLoader());
        try {
            setPaymentMethodType(method);

            if (method === 'card') {
                setHasFetchedCardFees(false);
                const today = dayjs().startOf('day');
                setPaymentRows(prev =>
                    prev.map(row => {
                        const cardRow = cardRows.find(r => r.contractId === row.contractId);
                        return {
                            ...row,
                            paymentDate: today,
                            amountChoice: 'invoice' as AmountChoice,
                            dueAmount: cardRow?.dueAmount ?? row.dueAmount,
                            scheduledAmount: cardRow?.scheduledAmount ?? row.scheduledAmount,
                            paymentAmount: cardRow?.paymentAmount ?? row.paymentAmount,
                        };
                    }),
                );
                await fetchCardFees();
            } else {
                await getBankAccounts();
                // Back to bank transfer: restore the single-invoice amounts the user arrived with
                // (rows for contracts without a single-invoice override come back unchanged).
                setPaymentRows(prev =>
                    prev.map(row => {
                        const bankRow = buildPaymentRow(row.contractId);
                        return {
                            ...row,
                            dueAmount: bankRow.dueAmount,
                            scheduledAmount: bankRow.scheduledAmount,
                            paymentAmount: bankRow.paymentAmount,
                        };
                    }),
                );
                // Restore any contracts the user had selected but that were filtered out of
                // paymentRows because they were not eligible for card payments. Bank Account
                // supports all selectable contracts, so the user's selection should be intact.
                setPaymentRows(prev => {
                    const present = new Set(prev.map(row => row.contractId));
                    const toRestore = userSelectedContractIdsRef.current.filter(
                        contractId => !present.has(contractId) && canSelectContract(contractId),
                    );
                    if (toRestore.length === 0) return prev;
                    setSelectedPaymentContracts((prevSelected: string[]) => [
                        ...new Set([...prevSelected, ...toRestore]),
                    ]);
                    return [...prev, ...toRestore.map(buildPaymentRow)];
                });
            }
        } finally {
            dispatch(hideLoader());
        }
    };

    const hasContractInvoices = (contractId: string): boolean => {
        const searchId = String(contractId).trim().toUpperCase();
        return openInvoices.some((inv: OpenInvoiceApiResponse) => {
            const aspireId = String(inv?.AspireContractId || inv?.aspireContractId || '')
                .trim()
                .toUpperCase();
            const dbId = String(inv?.contractId || inv?.ContractId || '')
                .trim()
                .toUpperCase();
            return aspireId === searchId || dbId === searchId;
        });
    };

    const totalAmount = paymentRows.reduce((sum, row, index) => {
        if (
            paymentMethodType === 'card' &&
            (cardFeeByRow[index]?.isDisabled || duplicatePaymentCheckByRow[index]?.isDuplicate)
        )
            return sum;
        const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
        const choice = row.amountChoice ?? 'invoice';
        const hasInvoices = hasContractInvoices(row.contractId);
        // Commercial (non-consumer-loan) bank-transfer contracts fall back to the outstanding
        // balance when there are no open invoices (see PaymentAmountCell's showsOutstandingBalanceFallback),
        // so their displayed Amount Due must still count toward the total.
        const showsOutstandingBalanceFallback = !isConsumerLoanContract(contract) && paymentMethodType !== 'card';

        // If "Full" (invoice) is selected, only count if there are actual open invoices, unless
        // this row falls back to the outstanding balance (commercial, bank transfer, no invoices).
        if (choice === 'invoice' && !hasInvoices && !showsOutstandingBalanceFallback) return sum;

        // getEffectivePaymentAmount now handles the correct calculation for all cases
        return sum + getEffectivePaymentAmount(row, contract ?? undefined);
    }, 0);

    // Whether any selected row's amount is under the $300 card-payment minimum (mirrors
    // usePaymentSubmission's validateCardPaymentMinimumAmount, which blocks submission per row).
    // Drives the info banner below so it only shows when it's actually relevant, instead of
    // unconditionally whenever paying by card.
    const hasCardPaymentBelowMinimum =
        paymentMethodType === 'card' &&
        paymentRows.some(row => {
            const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
            return getEffectivePaymentAmount(row, contract ?? undefined) < 300;
        });

    // Merge duplicate payment check with card fee info
    const mergedCardFeeByRow = useMemo(
        () =>
            cardFeeByRow.map((fee, index) => {
                const duplicateCheck = duplicatePaymentCheckByRow[index];
                if (duplicateCheck?.isDuplicate) {
                    return {
                        ...fee,
                        isDisabled: true,
                        infoMessage: duplicateCheck.infoMessage || t('makePayment.cardPaymentAlreadyScheduled'),
                    };
                }
                return fee;
            }),
        [cardFeeByRow, duplicatePaymentCheckByRow, t],
    );

    // Filter out contracts with zero outstanding balance from the ineligible contracts list
    const displayableIneligibleContracts = useMemo(() => {
        return cardIneligibleContracts.filter(contract => {
            const dbContractId = contract.Id?.toString() || '';
            const outstandingBalance = parseFloat(String(contract.OutstandingBalance || '0'));

            // Sum scheduled invoices (is_scheduled = true)
            const scheduledInvoices = openInvoices.reduce((sum: number, inv: OpenInvoiceApiResponse) => {
                const invDbId = String(inv?.contractId || inv?.ContractId || '')
                    .trim()
                    .toUpperCase();
                const checkDbId = dbContractId.trim().toUpperCase();
                const isScheduled = inv?.is_scheduled === true || inv?.IsScheduled === true;

                if (invDbId === checkDbId && isScheduled) {
                    return sum + parseFloat(String(inv.amount || inv.Amount || '0'));
                }
                return sum;
            }, 0);

            // Get pending schedule amount for this contract
            const pendingAmount = pendingScheduleAmount.reduce((sum: number, pending) => {
                if (String(pending.contractId).trim() === dbContractId.trim()) {
                    return sum + parseFloat(String(pending.totalPendingScheduleAmount || '0'));
                }
                return sum;
            }, 0);

            const totalScheduledAmount = scheduledInvoices + pendingAmount;
            return outstandingBalance > totalScheduledAmount;
        });
    }, [cardIneligibleContracts, openInvoices, pendingScheduleAmount]);

    // Check if any card row has an invalid card (non-credit or non-US)
    const hasCardValidationErrors = useMemo(() => {
        if (paymentMethodType !== 'card') return false;
        return paymentRows.some(row => {
            if (!row.paymentCardId) return false;
            const selectedCard = savedPaymentMethods.find(m => m.id === row.paymentCardId);
            if (!selectedCard) return false;
            const isCreditCard = selectedCard.funding === undefined || selectedCard.funding === 'credit';
            const isUSCard = selectedCard.country === undefined || selectedCard.country === 'US';
            return !isCreditCard || !isUSCard;
        });
    }, [paymentMethodType, paymentRows, savedPaymentMethods]);

    const handleAddCardOrAccountClick = useCallback(async () => {
        if (paymentMethodType === 'card') {
            try {
                const response = await createStripeSetupIntent(true);
                const secret = response?.clientSecret ?? response?.data?.clientSecret;
                if (secret) {
                    setSetupIntentClientSecret(secret);
                    setAddPaymentMethodModalOpen(true);
                } else {
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: t('makePayment.couldNotLoadPaymentForm'),
                            severity: 'error',
                        }),
                    );
                }
            } catch (err) {
                console.error('SetupIntent API error:', err);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: t('makePayment.couldNotStartAddCard'),
                        severity: 'error',
                    }),
                );
            }
        } else {
            setDrawerOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentMethodType, dispatch, t, setDrawerOpen]);

    return (
        <Stack height={{ xs: 'auto', md: '100%' }}>
            <Stack
                direction={'row'}
                height={{ xs: 'auto', md: '100%' }}
                flexWrap={{ xs: 'wrap', md: 'nowrap' }}
                pb={{ xs: '190px', md: 0 }}
            >
                {/* Left Sidebar - Search and Selected Contracts */}
                <ContractSidebar
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    isLoadingData={isLoadingData}
                    activeContracts={activeContracts}
                    canSelectContract={canSelectContract}
                    isCardUnavailableForContract={isCardUnavailableForContract}
                    paymentMethodType={paymentMethodType}
                    paymentRows={paymentRows}
                    setPaymentRows={setPaymentRows}
                    setSelectedPaymentContracts={setSelectedPaymentContracts}
                    userSelectedContractIdsRef={userSelectedContractIdsRef}
                    buildPaymentRow={buildPaymentRow}
                    setPaymentRowErrors={setPaymentRowErrors}
                    setPaymentSubmitErrors={setPaymentSubmitErrors}
                    handleBack={handleBack}
                    onPaymentMethodChange={handlePaymentMethodChange}
                    onAddCardOrAccountClick={handleAddCardOrAccountClick}
                    isStripeEnabled={isStripeEnabled}
                    hiddenContractIds={hiddenContractIds}
                    bankAccountOptions={bankAccountOptions}
                    savedPaymentMethods={savedPaymentMethods}
                    openInvoices={openInvoices}
                    resolveContractForPaymentRow={resolveContractForPaymentRow}
                    isConsumerLoanContract={isConsumerLoanContract}
                    getEffectivePaymentAmount={getEffectivePaymentAmount}
                    cardFeeByRow={mergedCardFeeByRow}
                    cardFeesLoading={cardFeesLoading}
                    getRowCardFeeAmount={getRowCardFeeAmount}
                    paymentRowErrors={paymentRowErrors}
                    paymentSubmitErrors={paymentSubmitErrors}
                    paymentDatePickerErrors={paymentDatePickerErrors}
                    handleBankAccountChange={handleBankAccountChange}
                    handlePaymentCardChange={handlePaymentCardChange}
                    handleDateChange={handleDateChange}
                    handleDatePickerError={handleDatePickerError}
                    handleAmountChoiceChange={handleAmountChoiceChange}
                    setOtherAmountInputRef={setOtherAmountInputRef}
                />

                {/* Right Content - Payment Rows */}
                <Stack
                    sx={{
                        width: '100%',
                        height: { xs: 'auto', md: '100%' },
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Stack
                        justifyContent={'space-between'}
                        gap={3}
                        sx={{ height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'auto' } }}
                    >
                        <Box
                            sx={paymentContentBoxSx}
                            pt={{ xs: 2, md: 3 }}
                            height={
                                !isLoadingData && !paymentRows.length && displayableIneligibleContracts.length === 0
                                    ? '100%'
                                    : 'auto'
                            }
                        >
                            {/* Payment Rows Content */}
                            <Stack
                                direction={'row'}
                                justifyContent="space-between"
                                alignItems={'center'}
                                gap={2}
                                flexWrap={'wrap'}
                                sx={{ display: { xs: 'none', md: 'flex' } }}
                            >
                                <Stack direction={'row'} alignItems={'center'} gap={2}>
                                    <CommonHeader
                                        icon="icon-arrow-left"
                                        cursorPointer="pointer"
                                        mainTitle={t('dashboard.makePayment.mainTitle')}
                                        subTitle={t('dashboard.makePayment.subTitle')}
                                        onclick={handleBack}
                                        iconBgColor={colors.fullWhite}
                                        iconColor={colors.primaryLight[100]}
                                        borderColor={colors.primaryLight[100]}
                                    />
                                </Stack>
                                {/* Header - Pay via (Card | Bank Account) & Add Card / Add Account - only when Stripe enabled */}
                                {isStripeEnabled ? (
                                    <PaymentMethodSelector
                                        variant="desktop"
                                        paymentMethodType={paymentMethodType}
                                        onPaymentMethodChange={handlePaymentMethodChange}
                                        onAddCardOrAccountClick={handleAddCardOrAccountClick}
                                        isStripeEnabled={isStripeEnabled}
                                        isImpersonating={impersonation?.isImpersonating}
                                    />
                                ) : (
                                    <ButtonComponent
                                        variant="contained"
                                        onClick={handleAddCardOrAccountClick}
                                        endIcon={<span className="icon-plus" />}
                                        sx={addAccountButtonSx}
                                    >
                                        {t('BankAccounts.addAccount')}
                                    </ButtonComponent>
                                )}
                            </Stack>
                            {impersonation?.isImpersonating && (
                                <Stack
                                    sx={{
                                        p: 1.5,
                                        mt: 2,
                                        backgroundColor: colors.warningPreset[40],
                                        borderRadius: 1,
                                        border: `1px solid ${colors.warningPreset[30]}`,
                                    }}
                                >
                                    <Typography variant="body2" color={colors.primaryLight[100]}>
                                        {t('payments.impersonatingNote') ||
                                            'The payment button is disabled while impersonating. You can review the payment details but cannot submit the payment.'}
                                    </Typography>
                                </Stack>
                            )}
                            <Box sx={{ mt: { xs: 0, md: 3 } }}>
                                {isLoadingData && !paymentRows.length ? (
                                    <Box sx={emptyStateBoxSx}>
                                        <Stack alignItems="center" justifyContent="center" spacing={2}>
                                            <Loader />
                                        </Stack>
                                    </Box>
                                ) : (
                                    <>
                                        {isStripeEnabled &&
                                            paymentMethodType === 'card' &&
                                            hasCardPaymentBelowMinimum && (
                                                <Box sx={cardMinimumAmountInfoBoxSx}>
                                                    <span className="icon-info-circle" style={{ fontSize: 16 }} />
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={500}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {t('makePayment.cardMinimumPaymentRequired')}
                                                    </Typography>
                                                </Box>
                                            )}
                                        {isStripeEnabled && paymentMethodType === 'card' && cardFeesError && (
                                            <Box sx={cardFeesErrorBoxSx}>
                                                <span className="icon-error" style={{ fontSize: 16 }} />
                                                <Typography
                                                    variant="overline"
                                                    fontWeight={700}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {t('makePayment.cardFeesUnavailable')}
                                                </Typography>
                                            </Box>
                                        )}
                                        {isStripeEnabled &&
                                            paymentMethodType === 'card' &&
                                            paymentRows.some(row => {
                                                const contract =
                                                    getContractById(row.contractId) ||
                                                    getContractById(row.dbContractId);
                                                return contract?.AutoPay === true;
                                            }) && (
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        mb: 2,
                                                        backgroundColor: colors.warningPreset[80],
                                                        borderRadius: 1,
                                                        border: `1px solid ${colors.warningPreset[30]}`,
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="flex-start" gap={1}>
                                                        <span
                                                            className="icon-info-circle"
                                                            style={{
                                                                fontSize: 16,
                                                                color: colors.warningPreset[40],
                                                                marginTop: 2,
                                                            }}
                                                        />
                                                        <Typography variant="body2" color={colors.warningPreset[10]}>
                                                            {t('makePayment.cardPaymentDisabledAutoPay') ||
                                                                'Card payments are not available for contracts with AutoPay enabled'}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            )}
                                        {filteredPaymentRows?.length > 0 && (
                                            <PaymentRowsTable
                                                paymentRows={filteredPaymentRows}
                                                paymentMethodType={paymentMethodType}
                                                cardFeeByRow={mergedCardFeeByRow}
                                                cardIneligibleContracts={cardIneligibleContracts}
                                                savedPaymentMethods={savedPaymentMethods}
                                                bankAccountOptions={bankAccountOptions}
                                                paymentRowErrors={paymentRowErrors}
                                                paymentSubmitErrors={paymentSubmitErrors}
                                                paymentDatePickerErrors={paymentDatePickerErrors}
                                                cardFeesLoading={cardFeesLoading}
                                                openInvoices={openInvoices}
                                                resolveContractForPaymentRow={resolveContractForPaymentRow}
                                                getContractById={getContractById}
                                                isConsumerLoanContract={isConsumerLoanContract}
                                                handlePaymentCardChange={handlePaymentCardChange}
                                                handleBankAccountChange={handleBankAccountChange}
                                                handleDateChange={handleDateChange}
                                                handleDatePickerError={handleDatePickerError}
                                                handleDeleteRow={handleDeleteRow}
                                                handleAmountChoiceChange={handleAmountChoiceChange}
                                                getRowCardFeeAmount={getRowCardFeeAmount}
                                                setOtherAmountInputRef={setOtherAmountInputRef}
                                                setPaymentRows={setPaymentRows}
                                                setPaymentRowErrors={setPaymentRowErrors}
                                                setPaymentSubmitErrors={setPaymentSubmitErrors}
                                                isStripeEnabled={isStripeEnabled}
                                            />
                                        )}
                                        {!paymentRows.length && (
                                            <Box sx={emptyStateBoxSx}>
                                                <Stack alignItems="center" justifyContent="center" spacing={0}>
                                                    <Typography variant="h6" color="textSecondary" textAlign={'center'}>
                                                        {t('dashboard.payments.noRecordsAvailable')}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        )}
                                        {paymentRows.length > 0 && totalAmount <= 0 && (
                                            <Box sx={emptyStateBoxSx}>
                                                <Stack alignItems="center" justifyContent="center" spacing={0}>
                                                    <Typography variant="h6" color="textSecondary" textAlign={'center'}>
                                                        {t('dashboard.payments.noRecordsAvailable')}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        )}
                                        {fullyPaidContracts.length > 0 && (
                                            <FullyPaidContractsPanel
                                                fullyPaidContracts={fullyPaidContracts}
                                                hasPaymentRows={paymentRows.length > 0}
                                            />
                                        )}
                                        {fullyCoveredContracts.length > 0 && (
                                            <FullyCoveredContractsPanel
                                                fullyCoveredContracts={fullyCoveredContracts}
                                                hasPaymentRows={paymentRows.length > 0}
                                            />
                                        )}
                                        {isStripeEnabled &&
                                            paymentMethodType === 'card' &&
                                            displayableIneligibleContracts.length > 0 && (
                                                <CardIneligibleContractsPanel
                                                    cardIneligibleContracts={displayableIneligibleContracts}
                                                    hasPaymentRows={paymentRows.length > 0}
                                                    activeContracts={activeContracts}
                                                />
                                            )}
                                    </>
                                )}
                            </Box>
                        </Box>
                        {/* Validation message for country mismatch */}
                        {hasCountryMismatch && (
                            <Box sx={{ color: colors.errorPreset[10], fontWeight: 500, textAlign: 'center', mb: 2 }}>
                                There are payment accounts set for countries that do NOT match the contract country.
                                Please update the selected payment accounts.
                            </Box>
                        )}

                        {/* Footer */}
                        <Stack
                            direction="row"
                            sx={{
                                p: { xs: 1.25, sm: 2 },
                                borderTop: `1px solid ${colors.greyPreset[30]}`,
                                justifyContent: 'space-between',
                                alignItems: { xs: 'center', sm: paymentMethodType === 'card' ? 'end' : 'center' },
                                gap: { xs: 1, sm: 2 },
                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                position: { xs: 'fixed', md: 'sticky' },
                                bottom: 0,
                                left: { xs: 0, md: 'auto' },
                                right: { xs: 0, md: 'auto' },
                                zIndex: { xs: 10, md: 1 },
                                backgroundColor: colors.fullWhite,
                                boxShadow: { xs: '0px -2px 8px rgba(0, 0, 0, 0.08)', md: 'none' },
                            }}
                        >
                            <Stack sx={paymentFooterLeftSx}>
                                {isStripeEnabled &&
                                paymentMethodType === 'card' &&
                                !hasCardValidationErrors &&
                                paymentRows.some(row => row.paymentCardId) ? (
                                    <Stack gap={{ xs: 0.5, sm: 0.5 }} width="auto">
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            flexWrap="wrap"
                                            gap={{ xs: 1, sm: 3 }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                minWidth={{ xs: 'auto', sm: 150 }}
                                            >
                                                {t('payments.paymentMethod.subtotal')} ({paymentRows.length}{' '}
                                                {t('payments.paymentMethod.contracts')})
                                            </Typography>
                                            <Typography
                                                variant="subtitle2"
                                                color="textPrimary"
                                                component={'span'}
                                                minWidth={{ xs: 'auto', sm: 50 }}
                                                textAlign="left"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {formatCurrency(totalAmount)}
                                            </Typography>
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            flexWrap="wrap"
                                            gap={{ xs: 1, sm: 3 }}
                                            color={colors.warningPreset[50]}
                                        >
                                            <Typography variant="caption" minWidth={{ xs: 'auto', sm: 150 }}>
                                                {t('makePayment.totalFeeLabel')}
                                                {cardFeesLoading && (
                                                    <Typography variant="caption" color="textSecondary" ml={0.5}>
                                                        …
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="subtitle2"
                                                minWidth={{ xs: 'auto', sm: 50 }}
                                                textAlign="left"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                +{formatCurrency(totalCardFee)}
                                            </Typography>
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            flexWrap="wrap"
                                            gap={{ xs: 1, sm: 3 }}
                                        >
                                            <Typography variant="subtitle2" color="textPrimary">
                                                {t('payments.paymentMethod.total')}
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                color="textPrimary"
                                                fontWeight={600}
                                                component={'span'}
                                                textAlign="left"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {formatCurrency(totalAmount + totalCardFee)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack gap={{ xs: 0.5, sm: 1 }} width="auto">
                                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                                            <Typography variant="caption" color="textSecondary">
                                                {t('payments.paymentMethod.subtotal')} ({paymentRows.length}{' '}
                                                {t('payments.paymentMethod.contracts')})
                                            </Typography>
                                            <Typography
                                                variant="subtitle2"
                                                color="textPrimary"
                                                component={'span'}
                                                textAlign="left"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {formatCurrency(totalAmount)}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                                            <Typography variant="subtitle2" color="textPrimary">
                                                {t('payments.paymentMethod.total')}
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                fontWeight={600}
                                                component="h2"
                                                color="textPrimary"
                                                textAlign="left"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {formatCurrency(totalAmount)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>
                            <Stack direction="row" gap={2} justifyContent="flex-end">
                                <ButtonComponent variant="outlined" onClick={handleCancel} sx={cancelButtonSx}>
                                    {t('common.Cancel')}
                                </ButtonComponent>
                                <ButtonComponent
                                    variant="contained"
                                    onClick={() =>
                                        paymentMethodType === 'card' && isStripeEnabled
                                            ? handlePayWithCard()
                                            : handleMakePayment()
                                    }
                                    disabled={
                                        impersonation?.isImpersonating ||
                                        paymentRows?.length === 0 ||
                                        totalAmount <= 0 ||
                                        !Number.isFinite(totalAmount) ||
                                        (paymentMethodType === 'card' && !isStripeEnabled) ||
                                        (paymentMethodType === 'bank_account'
                                            ? paymentRows?.some(row => !row?.bankAccount || !row?.paymentDate)
                                            : paymentRows?.some(row => !row?.paymentCardId || !row?.paymentDate)) ||
                                        paymentRowErrors?.some(
                                            error => error.bankAccount || error.paymentDate || error.paymentAmount,
                                        ) ||
                                        cardFeesError ||
                                        // Disable if any card rows have ineligible cards or an already-scheduled contract (merged disabled flag)
                                        (paymentMethodType === 'card' &&
                                            paymentRows?.some((row, idx) => mergedCardFeeByRow[idx]?.isDisabled)) ||
                                        // Disable if any card has validation errors (non-credit or non-US)
                                        hasCardValidationErrors
                                    }
                                    sx={payButtonSx}
                                >
                                    {t('dashboard.sections.Pay')}
                                </ButtonComponent>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>

            <AddPaymentMethodModal
                open={addPaymentMethodModalOpen}
                clientSecret={setupIntentClientSecret}
                onClose={() => {
                    setAddPaymentMethodModalOpen(false);
                    setSetupIntentClientSecret(null);
                }}
                onSuccess={() => {
                    setAddPaymentMethodModalOpen(false);
                    setSetupIntentClientSecret(null);
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: t('makePayment.paymentMethodSavedNextMonthEmi'),
                            severity: 'success',
                        }),
                    );
                    fetchStripePaymentMethods();
                }}
            />

            {/* Confirmation Modal */}
            <PaymentConfirmationModal
                open={showConfirmationModal}
                onClose={handleCloseConfirmationModal}
                onConfirm={handleConfirmPayment}
                paymentRows={paymentRows}
                totalAmount={totalAmount}
                userDetails={userDetails}
                bankAccountOptions={bankAccountOptions}
                getContractById={getContractById}
                getEffectivePaymentAmount={getEffectivePaymentAmount}
            />

            {/* Bank Account Modal */}
            <ApplyToAllModal
                open={showBankAccountModal}
                onClose={handleCloseBankAccountModal}
                title="Bank Account Selection"
                message="Would you like to apply this bank account to all selected contracts?"
                onNo={handleApplyBankAccountToCurrentOnly}
                onYes={handleApplyBankAccountToAll}
            />

            <ApplyToAllModal
                open={showApplyCardModal}
                onClose={handleCloseApplyCardModal}
                title="Payment Card Selection"
                message="Would you like to apply this card to all selected contracts?"
                onNo={handleApplyCardToCurrentOnly}
                onYes={handleApplyCardToAll}
            />

            {/* Add Account Drawer */}
            <AddBankAccountDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                formData={formData}
                formErrors={formErrors}
                setFormErrors={setFormErrors}
                country={country}
                setCountry={setCountry}
                onInputChange={handleInputChange}
                onAddAccount={handleAddAccount}
                accountTypeOptions={accountTypeOptions}
                showAccountNumber={showAccountNumber}
                setShowAccountNumber={setShowAccountNumber}
                showReEnterAccountNumber={showReEnterAccountNumber}
                setShowReEnterAccountNumber={setShowReEnterAccountNumber}
                showRoutingNumber={showRoutingNumber}
                setShowRoutingNumber={setShowRoutingNumber}
                showReEnterRoutingNumber={showReEnterRoutingNumber}
                setShowReEnterRoutingNumber={setShowReEnterRoutingNumber}
            />
        </Stack>
    );
};

export default DashboardMakePayment;
